import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUserData, setUserData, removeUserData, clearUserProfileCache, clearBusinessEventsCache, clearInfluencerEventsCache } from '../utils';
import { UserType } from '../constants';

export interface User {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  userType?: UserType;
}

export interface UserContextType {
  user: User | null;
  loading: boolean;
  login: (userData: User) => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = () => {
      try {
        const userData = getUserData();
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = (userData: User) => {
    setUserData(userData);
    setUser(userData);
  };

  const logout = () => {
    removeUserData();
    clearUserProfileCache(); // Clear profile cache on logout
    clearBusinessEventsCache(); // Clear all business events cache on logout
    clearInfluencerEventsCache(); // Clear influencer events cache on logout
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
